// web/src/pages/statements/StatementRunDetailPage.tsx
//
// I-10 — Statement Run detail (Plan III Task 24; migrated to DetailPage
// archetype in Plan IV Task 8).
//
// Spec §5.11: progress bar (live for Running runs via SignalR), counts,
// started/completed times, requester. Items table with account, channel,
// status, attempt count, error message, generated-PDF link, email-log link.
// Actions: Retry-failed (terminal w/ failures), Cancel (Running/Pending),
// Open print-tab (terminal w/ Generated/Delivered items).
import { useEffect } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/app/layouts/PageContainer";
import { DetailPage } from "@/archetypes/DetailPage";
import {
  BulkRunStatusBadge,
  BulkRunItemStatusBadge,
  BulkRunProgressBar,
} from "@/components/invoices-payments";
import {
  getStatementRun,
  listStatementRunItems,
  retryFailedStatementRun,
  cancelStatementRun,
  statementRunPrintTabUrl,
  downloadStatementPdf,
  buildRunHubConnection,
  type BulkStatementRun,
  type BulkStatementRunItem,
  type Paged,
  type RunStatusEvent,
  type ItemStatusEvent,
} from "@/lib/invoices-payments";
import { getAccessToken } from "@/lib/auth";

const TERMINAL: BulkStatementRun["status"][] = [
  "Completed",
  "CompletedWithErrors",
  "Cancelled",
];

export default function StatementRunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const id = Number(runId);
  const qc = useQueryClient();

  const runQuery = useQuery({
    queryKey: ["statement-run", id],
    queryFn: () => getStatementRun(id),
    retry: false,
    enabled: Number.isFinite(id),
  });

  const itemsQuery = useQuery({
    queryKey: ["statement-run-items", id],
    queryFn: () => listStatementRunItems(id, undefined, 1, 200),
    retry: false,
    enabled: Number.isFinite(id),
  });

  // SignalR live updates
  useEffect(() => {
    if (!Number.isFinite(id)) return;
    const conn = buildRunHubConnection(async () => getAccessToken());
    let disposed = false;

    conn.on("RunStatusChanged", (ev: RunStatusEvent) => {
      if (disposed || ev.runId !== id) return;
      qc.setQueryData<BulkStatementRun>(["statement-run", id], (prev) =>
        prev
          ? {
              ...prev,
              status: ev.status,
              accountCount: ev.accountCount,
              successCount: ev.successCount,
              failureCount: ev.failureCount,
              skippedCount: ev.skippedCount,
              completedAtUtc: ev.completedAtUtc,
            }
          : prev,
      );
    });

    conn.on("ItemStatusChanged", (ev: ItemStatusEvent) => {
      if (disposed || ev.runId !== id) return;
      qc.setQueryData<Paged<BulkStatementRunItem>>(
        ["statement-run-items", id],
        (prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((i) =>
                  i.runItemId === ev.runItemId
                    ? {
                        ...i,
                        status: ev.status,
                        attemptCount: ev.attemptCount,
                        errorMessage: ev.errorMessage,
                      }
                    : i,
                ),
              }
            : prev,
      );
    });

    conn
      .start()
      .then(() => conn.invoke("SubscribeToRun", id))
      .catch(() => {
        /* surfaced via auto-reconnect; non-fatal for the page */
      });

    return () => {
      disposed = true;
      conn.invoke("UnsubscribeFromRun", id).catch(() => {});
      conn.stop().catch(() => {});
    };
  }, [id, qc]);

  const retryMut = useMutation({
    mutationFn: () => retryFailedStatementRun(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["statement-run", id] });
      qc.invalidateQueries({ queryKey: ["statement-run-items", id] });
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelStatementRun(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["statement-run", id] });
    },
  });

  if (!Number.isFinite(id)) {
    return (
      <PageContainer>
        <div role="alert" className="text-red-600">Invalid run id.</div>
      </PageContainer>
    );
  }

  if (runQuery.isLoading) {
    return (
      <PageContainer>
        <div role="status">Loading run…</div>
      </PageContainer>
    );
  }

  if (runQuery.isError || !runQuery.data) {
    return (
      <PageContainer>
        <div role="alert" className="text-red-600">Could not load run. Retry?</div>
      </PageContainer>
    );
  }

  const run = runQuery.data;
  const isTerminal = TERMINAL.includes(run.status);
  const delivered =
    (itemsQuery.data?.items ?? []).filter(
      (i) => i.status === "Generated" || i.status === "Delivered",
    ).length;

  const showRetry = run.status === "CompletedWithErrors";
  const showCancel = run.status === "Pending" || run.status === "Running";
  const showPrintTab = isTerminal && delivered > 0;

  const headerActions = (
    <div className="flex flex-wrap items-center gap-2" data-testid="statement-run-actions">
      {showRetry && (
        <Button
          type="button"
          onClick={() => retryMut.mutate()}
          disabled={retryMut.isPending}
          data-testid="retry-failed-btn"
        >
          {retryMut.isPending ? "Retrying…" : "Retry failed"}
        </Button>
      )}
      {showCancel && (
        <Button
          type="button"
          variant="destructive"
          onClick={() => cancelMut.mutate()}
          disabled={cancelMut.isPending}
          data-testid="cancel-run-btn"
        >
          {cancelMut.isPending ? "Cancelling…" : "Cancel run"}
        </Button>
      )}
      {showPrintTab && (
        <a
          href={statementRunPrintTabUrl(run.runId)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="print-tab-link"
        >
          <Button type="button" variant="secondary">
            Open print tab
          </Button>
        </a>
      )}
    </div>
  );

  return (
    <DetailPage
      title={`Statement run #${run.runId}`}
      refPill={<BulkRunStatusBadge status={run.status} />}
      headerVariant="title"
      headerActions={headerActions}
      backTo="/statements/runs"
    >
      <div data-testid="statement-run-detail">
        {(retryMut.isError || cancelMut.isError) && (
          <div className="mb-4 space-y-1">
            {retryMut.isError && (
              <p className="text-sm text-destructive">Could not retry. Try again.</p>
            )}
            {cancelMut.isError && (
              <p className="text-sm text-destructive">Could not cancel. Try again.</p>
            )}
          </div>
        )}

        {/* Summary card */}
        <section className="mb-6 grid gap-4 rounded-md border p-4 md:grid-cols-2">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Started</dt>
            <dd>{new Date(run.startedAtUtc).toLocaleString()}</dd>
            <dt className="text-muted-foreground">Completed</dt>
            <dd>
              {run.completedAtUtc
                ? new Date(run.completedAtUtc).toLocaleString()
                : "—"}
            </dd>
            <dt className="text-muted-foreground">Requested by</dt>
            <dd>
              {run.requestedByUserId}
              {run.isRecurring && (
                <span className="ml-1 text-xs text-slate-400">(auto)</span>
              )}
            </dd>
            <dt className="text-muted-foreground">Delivery mode</dt>
            <dd>{run.deliveryMode}</dd>
            <dt className="text-muted-foreground">Accounts</dt>
            <dd>{run.accountCount}</dd>
            <dt className="text-muted-foreground">Success / Failed / Skipped</dt>
            <dd>
              {run.successCount} / {run.failureCount} / {run.skippedCount}
            </dd>
          </dl>
          <div>
            <div className="mb-2 text-xs text-muted-foreground">Progress</div>
            <BulkRunProgressBar
              accountCount={run.accountCount}
              successCount={run.successCount}
              failureCount={run.failureCount}
              skippedCount={run.skippedCount}
            />
            <div className="mt-2 text-xs text-muted-foreground">
              {run.successCount + run.failureCount + run.skippedCount} of{" "}
              {run.accountCount} processed
            </div>
          </div>
        </section>

        {/* Items table */}
        <section>
          <h2 className="mb-2 text-lg font-semibold">Items</h2>
          {itemsQuery.isLoading && <div role="status">Loading items…</div>}
          {itemsQuery.isError && (
            <div role="alert" className="text-red-600">
              Could not load items.
            </div>
          )}
          {itemsQuery.data && itemsQuery.data.items.length === 0 && (
            <div className="rounded-md border p-6 text-center text-sm text-slate-500">
              No items in this run.
            </div>
          )}
          {itemsQuery.data && itemsQuery.data.items.length > 0 && (
            <table className="w-full text-sm" data-testid="statement-run-items-table">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Account</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Error</th>
                  <th>PDF</th>
                  <th>Email log</th>
                </tr>
              </thead>
              <tbody>
                {itemsQuery.data.items.map((i) => (
                  <tr key={i.runItemId} className="border-t align-top">
                    <td className="py-2">
                      <Link
                        to={`/accounts/${i.accountId}`}
                        className="text-blue-600 hover:underline"
                      >
                        {i.accountReferenceNo ?? `#${i.accountId}`}
                      </Link>
                      {i.companyName && (
                        <div className="text-xs text-slate-500">{i.companyName}</div>
                      )}
                    </td>
                    <td>
                      <BulkRunItemStatusBadge status={i.status} />
                    </td>
                    <td>{i.attemptCount}</td>
                    <td className="max-w-[24rem] truncate text-xs text-red-600">
                      {i.errorMessage ?? "—"}
                    </td>
                    <td>
                      {i.statementId != null ? (
                        <button
                          type="button"
                          className="text-blue-600 hover:underline"
                          onClick={() => void downloadStatementPdf(id, i.statementId!)}
                        >
                          Download
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {i.emailLogId != null ? (
                        <span className="text-xs text-slate-600">
                          #{i.emailLogId}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </DetailPage>
  );
}
